import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import Nav from "./components/common/Nav";
import PrivateRoute from "./components/common/PrivateRoute";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrganizationPage from "./components/organizations/OrganizationPage";
import SourcesPage from "./components/sources/SourcesPage";
import ConnectionsPage from "./components/connections/ConnectionsPage";
import AccountPage from "./components/account/AccountPage";
import { ManageOrganizationDetails } from "./components/organizations/ManageOrganizationDetails";
import DestinationsPage from "./components/destinations/DestinationsPage"
import { ManageConnectionDetails } from "./components/connections/ManageConnectionDetails";
import { ManageAddConnection } from "./components/connections/ManageAddConnection";
import { useKeycloakToken, LoginModal } from './components/common/useKeycloak';
import { ManageDestinationDetails } from "./components/destinations/ManageDestinationDetails";
import { ManageAddConnectionStepOne } from "./components/connections/ManageAddConnectionStepOne";
import { ManageAddConnectionStepTwo } from "./components/connections/ManageAddConnectionStepTwo";
import { ManageAddConnectionStepThree } from "./components/connections/ManageAddConnectionStepThree";
import { ManageDestinationModal } from "./components/destinations/ManageDestinationModal";
import { HelpProvider } from "../context/HelpContext";
import HelpSidePanel from "./components/common/HelpSidePanel";
import { setAnalyticsUserProperties } from "./analytics/analytics";
import { useVersionBannerVisible } from "../components/common/VersionBanner";
import { RequireContactEmailGate } from "./auth/useRequireContactEmail";
import NotFoundPage from "./components/common/NotFoundPage";

// GUNDI-5639: this is e4ae89c's App.tsx with the provider shell removed — the
// real shell (Keycloak, Timezone, BrowserRouter) lives in src/App.tsx and
// mounts this tree whenever the version flag is not "new" — which, since
// GUNDI-5642, is the default and so the eagerly-imported tree. Everything below
// the providers is verbatim: the legacy route table, its own Nav, help panel,
// toasts and 404. No pageview tracking (the legacy titles predate the rename).
//
// TimezoneContext, HelpContext and i18next are imported from OUTSIDE this
// folder on purpose: a duplicated context module is a second context object,
// which the shell's provider cannot reach, and a second i18next instance would
// carry its own bundle. Everything else here is a legacy-only copy.

function LegacyApp() {
  // GUNDI-5665: true now — the legacy banner is back on, so the offset is
  // e4ae89c's 115px plus the 28px strip. Still read through the shared hook so
  // the strip and this compensation can never disagree.
  const bannerVisible = useVersionBannerVisible("legacy");
  return (
    <div className="min-h-screen bg-very-light-gray">
      <HelpProvider>
              <RequireContactEmailGate />
              <Nav />
              <HelpSidePanel />
              {/* pt-[143px] = e4ae89c's 115px + the 28px version banner inside
                  the fixed nav wrapper (trial-only, reverts at sunset). */}
              <div className={`px-8 desktop:px-[60px] py-6 ${bannerVisible ? "pt-[143px]" : "pt-[115px]"}`}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <ConnectionsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <AccountPage />
                      </PrivateRoute>
                    } />
                  
                  {/* workspaces */}
                  <Route
                    path="/workspaces"
                    element={
                      <PrivateRoute>
                        <OrganizationPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/workspaces/:org_id"
                    element={
                      <PrivateRoute>
                        <ManageOrganizationDetails />
                      </PrivateRoute>
                    } />
                  
                  {/* sources */}
                  <Route
                    path="/sources" // sources (list)
                    element={
                      <PrivateRoute>
                        <SourcesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  
                  {/* Destinations */}
                   <Route
                    path="/destinations"
                    element={
                      <PrivateRoute>
                        <DestinationsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/new"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destinations/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />

                  {/* Connections */}
                  <Route
                    path="/connections/:connection_id"
                    element={
                      <PrivateRoute>
                        <ManageConnectionDetails />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage"
                    element={
                      <PrivateRoute>
                        <ManageConnectionDetails />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:connection_id/:subpage/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />

                  {/* Add connection flow  */}
                  <Route
                    path="/connections/new"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepOne />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/configureprovider/:pt_value"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepTwo />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations"
                    element={
                      <PrivateRoute>
                        <ManageAddConnectionStepThree />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new/:connection_id/configuredestinations/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <div>
                          <ManageDestinationDetails />
                        </div>
                      </PrivateRoute>
                    } />

                  {/* Catch-all 404 */}
                  <Route
                    path="*"
                    element={
                      <PrivateRoute>
                        <NotFoundPage />
                      </PrivateRoute>
                    } />
                </Routes>
                <ToastContainer />
                <Content />
              </div>
      </HelpProvider>
    </div>
  );
}

function Content() {
  const [token, showLoginModal, handleLogin, isSessionExpired] = useKeycloakToken();

  useEffect(() => {
    if (token && typeof token === 'string') {
      setAnalyticsUserProperties(token);
    }
  }, [token]);

  return (
    <>
      {(showLoginModal === true) && <LoginModal showLoginModal={showLoginModal} handleLogin={handleLogin} isSessionExpired={isSessionExpired} />}
    </>
  );
}

export default LegacyApp;
